import { Directive, ElementRef, ViewChild, inject } from '@angular/core';
import { Checkout } from '@df/checkout/checkout.interfaces';
import { Status } from '@df/core/status';
import { AbstractBraintreeComponent } from '@df/module-braintree/abstract-braintree.component';
import { Braintree } from '@df/module-braintree/braintree.namespace';
import { Paypal } from '@df/module-paypal/paypal.namespace';
import { PaypalService } from '@df/module-paypal/paypal.service';

@Directive()
export abstract class BraintreePaypalCheckoutComponentDef extends AbstractBraintreeComponent<Braintree.IPaypalCheckoutPaymentMethod> {
    paypalSubmitStatus = new Status();
    /**
     * Payment method code related to component
     * @type {string}
     */
    protected readonly methodCode = Braintree.Methods.PaypalCheckout;

    protected paypalCheckoutInstance: Braintree.IPaypalCheckoutInstance | undefined;

    protected dataCollectorInstance: Braintree.IDataCollectorInstance | undefined;

    protected paypalService = inject(PaypalService);

    /**
     * Use vault flow model
     * @type {boolean}
     */
    private _useVault?: boolean;

    /**
     * Paypal checkout button containser
     * you need to put #paypalcheckoutbutton on div element in template
     */
    @ViewChild('paypalcheckoutbutton')
    protected paypalCheckoutButton?: ElementRef<any>;

    override ngOnInit() {
        super.ngOnInit();
        this.subscriptions.add(
            this.app.events.on(Checkout.EVENTS.SubmitSuccess).subscribe((data: Checkout.ISubmitSuccessEventData) => {
                this.paypalSubmitStatus.busy = false;
            })
        );
    }

    //////////////////////////////////////////
    /////
    /////   Paypal button container methods
    /////
    //////////////////////////////////////////

    /**
     * Id for paypal button container
     */
    get buttonId(): string {
        return `paypal-button-${this.componentId}`;
    }

    /**
     * Selector for paypal button container
     * @returns {string}
     */
    protected get buttonSelector(): string {
        return `#${this.buttonId}`;
    }

    /**
     * Returns paypal container element
     * @returns {HTMLElement | undefined}
     */
    protected get paypalCheckoutButtonElement(): HTMLElement | undefined {
        return this.paypalCheckoutButton && this.paypalCheckoutButton.nativeElement;
    }

    //////////////////////////////////////////
    /////
    /////   Payment method configuration
    /////
    //////////////////////////////////////////

    /**
     * if we should use vault flow
     * @returns {boolean}
     */
    protected get useVault(): boolean {
        if (this._useVault === undefined) {
            this._useVault = !!(this.method?.config && this.method.config.vault);
        }

        return this._useVault;
    }

    /**
     * if we should offer paypal credit
     * @returns {boolean}
     */
    protected get offerCredit(): boolean {
        return !!(this.method?.config && this.method.config.canUseCredit);
    }

    /**
     * Returns paypal checkout method configuration
     * @returns {Braintree.IPaypalCheckoutPaymentMethod}
     */
    protected get config(): Braintree.IPaypalCheckoutPaymentMethodConfig {
        return this.method!.config;
    }

    /**
     * Returns locale for button
     * @returns {Paypal.ButtonLocale}
     */
    protected getLocale(): Paypal.ButtonLocale {
        return this.config.locale || (this.app.data.locale as Paypal.ButtonLocale);
    }

    //////////////////////////////////////////
    /////
    /////   Script methods
    /////
    //////////////////////////////////////////

    /**
     * Returns required scripts for payment method
     * @returns {string[]}
     */
    protected getJsAssets(): string[] {
        return [
            Braintree.Scripts.CLIENT,
            Braintree.Scripts.PAYPAL_CHECKOUT,
            Paypal.SCRIPTS.CHECKOUT,
            this.useVault ? Braintree.Scripts.DATA_COLLECTOR : undefined
        ].filter(i => !!i) as string[];
    }

    /**
     * On script loasd success callback
     */
    protected override onScriptLoadSuccess(): void {
        const paypalCheckout = this.braintreeService.getPaypalCheckout();
        const dataCollector = this.braintreeService.getDataCollector();
        const client = this.braintreeService.getClient();
        const paypalButton = this.paypalService.getButton();

        const dataCollectorRequired = this.useVault;

        if (paypalCheckout && client && paypalButton) {
            // if data collecotr is required but not loaded, we throw script error
            if (dataCollectorRequired && !dataCollector) {
                this.onScriptLoadError();
            } else {
                // we first create client instance to create paypalCheckout instance and dataCollector instance if required
                client
                    .create({ authorization: this.getClientToken() })
                    .then(clientInstance => {
                        const onSuccess = (
                            paypalInstance: Braintree.IPaypalCheckoutInstance,
                            dataCollectorInstance?: Braintree.IDataCollectorInstance
                        ) => {
                            super.onScriptLoadSuccess();
                            this.paypalCheckoutInstance = paypalInstance;
                            this.dataCollectorInstance = dataCollectorInstance;
                            // if everything correctly loaded and created we reder button
                            this.renderButton();
                        };

                        if (dataCollectorRequired) {
                            Promise.all([
                                paypalCheckout.create({ client: clientInstance }),
                                dataCollector!.create({ client: clientInstance, paypal: true })
                            ])
                                .then(response => onSuccess(response[0], response[1]))
                                .catch(() => this.onScriptLoadError());
                        } else {
                            paypalCheckout
                                .create({ client: clientInstance })
                                .then(instance => onSuccess(instance))
                                .catch(() => this.onScriptLoadError());
                        }
                    })
                    .catch(() => this.onScriptLoadError());
            }
        } else {
            this.onScriptLoadError();
        }
    }

    //////////////////////////////////////////
    /////
    /////   Checkout
    /////
    //////////////////////////////////////////

    /**
     * Returns if checkout is valid
     * @returns {boolean}
     */
    isCheckoutValid(): boolean {
        return this.checkoutFormComponent.isCheckoutValid;
    }

    /**
     * On click callback
     * if checkout is not valid we validate checkout (scroll to error)
     */
    onClick(): void {
        if (!this.isCheckoutValid()) {
            this.checkoutFormComponent.validateCheckout();
        }
    }

    /**
     * On checkout submit error callback
     * @param {Checkout.ISubmitErrorEventData} data
     */
    protected onCheckoutError(data: Checkout.ISubmitErrorEventData): void {
        this.paypalSubmitStatus.busy = false;
    }

    /**
     * On checkout submit callback
     * @param {Checkout.ISubmitBeforeEventData} checkoutSubmitBeforeEventData
     */
    protected override onCheckoutSubmit(checkoutSubmitBeforeEventData: Checkout.ISubmitBeforeEventData): false | void {
        if (super.onCheckoutSubmit(checkoutSubmitBeforeEventData) === false) {
            return false;
        }
        this.paypalSubmitStatus.busy = true;
    }

    /**
     * Returns checkout susmission data
     * @param {Braintree.IPaypalCheckoutTokenizePayload} payload
     * @returns {Checkout.ISubmissionData}
     */
    protected getSubmissionData(payload: Braintree.IPaypalCheckoutTokenizePayload): Checkout.ISubmissionData {
        const data = this.checkoutFormComponent.getSubmissionData();

        const paymentData: Braintree.IPaypalCheckoutPaymentSubmissionData = {
            method: this.methodCode,
            additional_data: {
                payment_method_nonce: payload.nonce
            }
        };

        if (this.useVault && this.dataCollectorInstance) {
            paymentData.additional_data.device_data = this.dataCollectorInstance.deviceData;
        }

        data.paymentMethod = paymentData;
        return data;
    }

    //////////////////////////////////////////
    /////
    /////   Paypal button methods
    /////
    //////////////////////////////////////////

    /**
     * Render paypal button
     */
    protected renderButton(): void {
        this.clearButton();
        const button = this.paypalService.getButton();
        if (this.paypalCheckoutInstance && button) {
            button.render(this.getButtonRenderOptions(), this.buttonSelector);
        }
    }

    /**
     * Clears paypal button contaer element
     */
    protected clearButton(): void {
        if (this.paypalCheckoutButtonElement) {
            this.paypalCheckoutButtonElement.innerHTML = '';
        }
    }

    /**
     * Returns paypal button render options
     * @returns {Paypal.IButtonRenderConfiguration}
     */
    protected getButtonRenderOptions(): Paypal.IButtonRenderConfiguration {
        const { environment, style } = this.config;

        return {
            env: environment,
            locale: this.getLocale(),
            commit: true,
            style,
            payment: () => {
                return this.paypalCheckoutInstance!.createPayment(this.getPaymentOptions());
            },
            onAuthorize: data => {
                this.paypalCheckoutInstance!.tokenizePayment(data)
                    .then(payload => {
                        this.checkoutFormComponent.submitWithData(this.getSubmissionData(payload));
                    })
                    .catch(() => {
                        this.checkoutFormComponent.status.error = this.braintreeService.generalErrorMessage;
                    });
            },
            onCancel: () => {},
            onError: err => {
                this.checkoutFormComponent.status.error = this.braintreeService.getPaypalCheckoutErrorMessage(err);
            }
        };
    }

    //////////////////////////////////////////
    /////
    /////   Paypal braintree methods
    /////
    //////////////////////////////////////////

    /**
     * Returns paypal payment options
     * @returns {Braintree.IPaypalCheckoutCreatePaymentOptions<Braintree.PaypalCheckoutFlow>}
     */
    protected getPaymentOptions(): Braintree.IPaypalCheckoutCreatePaymentOptions<Braintree.PaypalCheckoutFlow> {
        const vault = this.useVault;
        const shippingAddressRequired = !this.checkoutFormComponent.isVirtual;
        return {
            flow: vault ? Braintree.PaypalCheckoutFlow.VAULT : Braintree.PaypalCheckoutFlow.CHECKOUT,
            offerCredit: this.offerCredit,
            currency: this.multisite.current.currency,
            amount: this.getTotal(),
            locale: this.getLocale(),
            enableShippingAddress: shippingAddressRequired,
            shippingAddressEditable: false,
            shippingAddressOverride: shippingAddressRequired ? this.getShippingAddress() : undefined
        };
    }

    /**
     * Returns entered shipping address
     * @returns {Braintree.IPaypalCheckoutCreatePaymentShippingAddress}
     */
    protected getShippingAddress(): Braintree.IPaypalCheckoutCreatePaymentShippingAddress | undefined {
        const address = this.checkoutFormComponent.shippingAddress;
        if (address) {
            return {
                line1: address.street_1 || '',
                line2: address.street_2,
                city: address.city || '',
                state: address.region,
                postalCode: address.postcode || '',
                countryCode: address.country_id || '',
                phone: address.telephone,
                recipientName: `${address.firstname || ''} ${address.lastname}`.trim()
            };
        } else {
            return undefined;
        }
    }
}
